import { Button, Radio, TextInput } from '@any-ui-react/core';
import { type CountryCode, getCountries, getCountryCallingCode } from 'libphonenumber-js';
import { useEffect, useMemo, useRef, useState } from 'react';

import 'country-flag-icons/3x2/flags.css';

import { HeaderTr, Table, Thead } from '@any-ui-react/table';
import classNames from 'classnames';
import { ModalFooter } from '@/common/components/shared/modal-footer';
import { useListCountriesQuery } from '@/common/graphql';

interface CountryItem {
  code: CountryCode;
  name: string;
  isd: string;
}

// Only codes libphonenumber-js itself recognizes can be dialed/parsed downstream (`PhoneNumberInput`'s
// `getCountryCallingCode`/`parsePhoneNumberWithError` calls) - the backend's Country master can list
// territories libphonenumber-js has no calling-code metadata for, so those are filtered out here.
const PHONE_SUPPORTED_COUNTRY_CODES = new Set<string>(getCountries());

export const CountryPickerModalContent = ({
  initialCountryCode,
  preferredCountryCode,
  onApply,
  onCancel,
}: {
  initialCountryCode?: CountryCode;
  // A country to sort near the top (after the currently-set one) when known - e.g. the edited
  // employee's current main contract company's country, or the company being chosen in Add mode.
  preferredCountryCode?: CountryCode;
  onApply: (countryCode: CountryCode) => void;
  onCancel: () => void;
}) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string>(initialCountryCode ?? '');
  const selectedRowRef = useRef<HTMLTableRowElement>(null);

  const { data, loading } = useListCountriesQuery({
    variables: { pageNumber: null, pageSize: null, keyword: null },
    fetchPolicy: 'cache-first',
  });

  // Base list/order comes straight from `listCountries` (backend order is authoritative) - only the
  // two priority exceptions below reorder it.
  const backendCountries = useMemo<CountryItem[]>(
    () =>
      (data?.listCountries.items ?? [])
        .filter(it => PHONE_SUPPORTED_COUNTRY_CODES.has(it.id))
        .map(it => ({
          code: it.id as CountryCode,
          name: it.name,
          isd: it.isdCode ?? `+${getCountryCallingCode(it.id as CountryCode)}`,
        })),
    [data],
  );

  // 1. the currently set country code (if any) comes first, 2. the preferred country comes next
  // (if different from 1), 3. everything else keeps listCountries' order.
  const allCountries = useMemo<CountryItem[]>(() => {
    const priorityCodes = [initialCountryCode, preferredCountryCode].filter(
      (code, index, arr): code is CountryCode => !!code && arr.indexOf(code) === index,
    );
    const priorityItems = priorityCodes
      .map(code => backendCountries.find(country => country.code === code))
      .filter((country): country is CountryItem => !!country);
    const priorityCodeSet = new Set(priorityItems.map(country => country.code));
    return [...priorityItems, ...backendCountries.filter(country => !priorityCodeSet.has(country.code))];
  }, [backendCountries, initialCountryCode, preferredCountryCode]);

  useEffect(() => {
    selectedRowRef.current?.scrollIntoView({ block: 'center' });
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return allCountries;
    return allCountries.filter(
      c => c.name.toLowerCase().includes(q) || c.isd.includes(q) || c.code.toLowerCase().includes(q),
    );
  }, [search, allCountries]);

  return (
    <>
      <TextInput
        autoFocus={true}
        placeholder="Search country or calling code..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="mb-3"
      />
      <div className="max-h-72 overflow-y-auto">
        <Radio.Group value={selected} onChange={setSelected}>
          <Table className="w-full text-sm">
            <Thead className="sticky top-0 z-10 bg-white">
              <HeaderTr className="border-b border-gray-2 text-left text-xs">
                <th className="w-8 py-1" />
                <th className="w-20 py-1">Code</th>
                <th className="py-1">Country</th>
              </HeaderTr>
            </Thead>
            <tbody>
              {loading && filtered.length === 0 && (
                <tr>
                  <td className="py-2 text-center text-gray-5" colSpan={3}>
                    Loading...
                  </td>
                </tr>
              )}
              {filtered.map(country => (
                <tr
                  key={country.code}
                  ref={country.code === selected ? selectedRowRef : undefined}
                  className={classNames('cursor-pointer hover:bg-gray-1', { 'bg-gray-1': country.code === selected })}
                  onClick={() => setSelected(country.code)}
                >
                  <td className="py-1.5 px-2">
                    <Radio value={country.code} />
                  </td>
                  <td className="py-1.5 tabular-nums">{country.isd}</td>
                  <td className="py-1.5">
                    <span className="flex items-center gap-x-2">
                      <span className={`flag:${country.code} shrink-0 border border-gray-4`} />
                      {country.name}
                    </span>
                  </td>
                </tr>
              ))}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td className="py-2 text-center text-gray-5" colSpan={3}>
                    No results
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Radio.Group>
      </div>
      <ModalFooter spacerSize={-0.5}>
        <Button variant="default" onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={!selected} onClick={() => selected && onApply(selected as CountryCode)}>
          Apply
        </Button>
      </ModalFooter>
    </>
  );
};
